import {APIClient} from './APIClient';

/**
 * «Algo de la puesta en marcha pudo haberse cumplido»: el aviso que tilda solo el drawer «Tus primeros
 * pasos» (card 5Dta3i4J).
 *
 * El estado de los pasos lo calcula el backend y la tarjeta del menú lo pide con cache de 30 s. Sin
 * este aviso, guardar el primer informe dejaba el drawer en «4 de 6» hasta que el usuario lo abría
 * (grabación del 29/09, 48:50 → 49:00). Lo llaman los puntos de la app que completan un paso —guardar
 * un informe, crear una línea, cargar memoria— y sólo si el pedido salió bien.
 *
 * Es un evento del `window` y no un contexto de React porque quienes lo disparan viven lejos del menú
 * (capas de API, pantallas que no lo contienen) y no hay por qué atarlos a un provider. No hay polling:
 * la tarjeta vuelve a preguntar una vez por aviso.
 *
 * Vive en `utils/` y no en `components/onboarding/` para que una capa de API (`api/clientMemoryApi`)
 * pueda llamarlo sin depender de un componente.
 */

export const ONBOARDING_PROGRESS_EVENT = 'dashbo:onboarding-progress';

// La misma URL que pide `fetchOnboardingStatus` (`components/onboarding/api.js`), sin `API_URL`:
// `APIClient.clearCache` borra por substring.
const ONBOARDING_STATUS_URL = 'agency/onboarding/status/';
// Y la de «Aprende Dashbo» (`api/learnApi.js`, `LEARNING_ME_PATH`): el mismo aviso tilda sus tareas
// (contrato §6 de la tanda 2026-10-03-aprende-dashbo). Escrita acá y no importada para que este módulo
// siga sin depender de la capa de una pantalla.
const LEARNING_ME_URL = 'learning/me/';

/**
 * Olvidar la copia cacheada del estado. Pedirlo `{cache: false}` NO alcanza: saltea la lectura pero no
 * reescribe lo guardado, así que la tarjeta —que se monta en cada navegación con `cache: true`— seguía
 * sirviendo la respuesta anterior durante el TTL. Vale igual para el lanzador de «Aprende Dashbo».
 */
export function forgetOnboardingStatus() {
    APIClient.clearCache(ONBOARDING_STATUS_URL);
    APIClient.clearCache(LEARNING_ME_URL);
}

/**
 * Para encadenar en la promesa de un pedido: `.then(notifyOnboardingProgress)`. Devuelve la respuesta
 * tal cual, y sólo avisa si salió bien — un error no completó nada.
 */
export function notifyOnboardingProgress(response) {
    if (response?.status === 'OK') {
        forgetOnboardingStatus();
        if (typeof window !== 'undefined') window.dispatchEvent(new Event(ONBOARDING_PROGRESS_EVENT));
    }
    return response;
}

/** Engancharse al aviso. Devuelve la función que lo desengancha. */
export function onOnboardingProgress(handler) {
    window.addEventListener(ONBOARDING_PROGRESS_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_PROGRESS_EVENT, handler);
}
